// CARO. Casa de estilo — central site configuration.
// Content and structural config live here (source of truth #2, after verified
// manufacturer data and before the D1 database — see docs/content-model.md).

import type { Locale } from '@/types';

export const BRAND = {
  /** Always write the brand exactly like this. Do not rename. */
  name: 'CARO. Casa de estilo',
  shortName: 'CARO',
  tagline: 'Estilo para la mujer que evoluciona.',
  supportingLine: 'Estilo · Belleza · Bienestar',
  domain: 'carocasadeestilo.com',
  productionUrl: 'https://carocasadeestilo.com',
  instagramHandle: 'carolina_collaer',
  instagramUrl: 'https://www.instagram.com/carolina_collaer/',
  contactEmail: 'info@carocasadeestilo.com',
  /** WhatsApp (display + wa.me link, digits only with country code). */
  whatsappDisplay: '+34 631 87 12 67',
  whatsappUrl: 'https://wa.me/34631871267',
} as const;

/**
 * Carolina, the public author of The Edit and curator of the house: the
 * schema.org `Person` (article author, /caro ProfilePage). Public brand name
 * only, never the legal identity in BUSINESS. `jobTitle` is the first segment
 * of each locale's `about.tags` (duplicated here because the dictionary imports
 * this module; tests/person.test.ts asserts they match). `path` is the logical
 * (unlocalized) profile page; `image` is the portrait painted on /caro.
 */
export const CARO_PERSON = {
  name: 'Carolina Collaer',
  jobTitle: {
    es: 'Asesora de imagen',
    en: 'Image consultant',
    fr: 'Conseillère en image',
  } as Record<Locale, string>,
  path: '/caro',
  image: '/media/caro/carolina-collaer-caro-casa-de-estilo-portada.webp',
  sameAs: [BRAND.instagramUrl] as readonly string[],
} as const;

/**
 * Where Carolina works in person (image consulting, personal shopping): the
 * single source for the «Dónde trabajo» block on /caro/servicios (page and
 * prerender) and for the schema.org `areaServed` nodes. `name` is the visible
 * label, the same in every locale; `places` are the schema.org nodes behind it
 * (the Penedès is two comarcas). Online and travel are dictionary copy.
 */
export const SERVICE_AREA: readonly {
  name: string;
  places: readonly { type: 'City' | 'AdministrativeArea'; name: string }[];
}[] = [
  { name: 'Sitges', places: [{ type: 'City', name: 'Sitges' }] },
  { name: 'Garraf', places: [{ type: 'AdministrativeArea', name: 'Garraf' }] },
  {
    name: 'Penedès',
    places: [
      { type: 'AdministrativeArea', name: 'Alt Penedès' },
      { type: 'AdministrativeArea', name: 'Baix Penedès' },
    ],
  },
  { name: 'Barcelona', places: [{ type: 'City', name: 'Barcelona' }] },
];

/**
 * Carolina's opening hours (owner, 2026-10-22): Monday to Saturday 10:00-17:00,
 * other times by appointment. The single source for the ProfessionalService
 * `openingHoursSpecification`; the visible line is dictionary copy
 * (`common.hours`, kept in step by a test). Same hours on the Google profile
 * and every directory (NAP consistency).
 */
export const OPENING_HOURS = {
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  opens: '10:00',
  closes: '17:00',
} as const;

/**
 * Public URL of CARO's Perfil de Empresa de Google (Google Business Profile,
 * ficha «CARO. casa de Estilo»), in the stable `?cid=` form. Goes in the
 * ProfessionalService `sameAs` on /caro/servicios (plan SEO local, D4).
 */
export const GOOGLE_BUSINESS_PROFILE_URL: string = 'https://maps.google.com/?cid=3257102822418949750';

/**
 * «Pedir reseñas» link of the same profile: opens the review window directly.
 * Used by the review-request email (docs/plan-bandeja-emails.md), the QR on
 * the parcel card and Carolina's WhatsApp message.
 */
export const GOOGLE_REVIEW_URL = 'https://g.page/r/CXYmNJfCjTMtEBM/review';

/**
 * Legal business identity (titular / responsable del tratamiento).
 * Real data provided by Carolina — used in the Legal Notice, Privacy policy,
 * footer and Contact. The legal *wording* of the policies still needs
 * professional review; this identity data is verified.
 */
export const BUSINESS = {
  legalName: 'Carolina Arvelo',
  regime: 'Autónoma',
  taxIdLabel: 'NIF/CIF',
  taxId: 'Z1202616C',
  address: {
    line1: "98 CL de l'Hospitalet",
    postalCode: '08734',
    locality: 'Can Trabal, Olèrdola',
    province: 'Barcelona',
    country: 'España',
  },
  email: 'info@carocasadeestilo.com',
} as const;

export function businessAddressLine(): string {
  const a = BUSINESS.address;
  return `${a.line1}, ${a.postalCode} ${a.locality}, ${a.province}, ${a.country}`;
}

// French renderings of the Spanish identity literals above (fallback: the
// original), for the /fr legal documents and contact page.
const COUNTRY_FR: Record<string, string> = { España: 'Espagne' };
const REGIME_FR: Record<string, string> = {
  Autónoma: 'travailleuse indépendante (autónoma)',
};

/** BUSINESS.regime in French (e.g. Autónoma → travailleuse indépendante). */
export const REGIME_LABEL_FR = REGIME_FR[BUSINESS.regime] ?? BUSINESS.regime;

/** businessAddressLine() with the country in French. */
export function businessAddressLineFr(): string {
  const a = BUSINESS.address;
  const country = COUNTRY_FR[a.country] ?? a.country;
  return `${a.line1}, ${a.postalCode} ${a.locality}, ${a.province}, ${country}`;
}

// English renderings of the same literals, for the /en legal documents.
const COUNTRY_EN: Record<string, string> = { España: 'Spain' };
const REGIME_EN: Record<string, string> = {
  Autónoma: 'self-employed (autónoma)',
};

/** BUSINESS.regime in English (e.g. Autónoma → self-employed). */
export const REGIME_LABEL_EN = REGIME_EN[BUSINESS.regime] ?? BUSINESS.regime;

/** businessAddressLine() with the country in English. */
export function businessAddressLineEn(): string {
  const a = BUSINESS.address;
  const country = COUNTRY_EN[a.country] ?? a.country;
  return `${a.line1}, ${a.postalCode} ${a.locality}, ${a.province}, ${country}`;
}

export const CURRENCY = 'EUR';

/** EU-27 (customs union). Outside these, the buyer pays customs duties/taxes. */
export const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

export function isEU(country: string): boolean {
  return EU_COUNTRIES.has(country.toUpperCase());
}

export const LOCALES: Locale[] = ['es', 'en', 'fr'];
export const DEFAULT_LOCALE: Locale = 'es';

/**
 * Primary navigation. Kept intentionally lean — curation over catalogue size.
 * `href` values are locale-agnostic paths; the router can be prefixed with a
 * locale segment later (/en/..., /fr/...) without changing this structure.
 */
export interface NavItem {
  label: string;
  /** i18n key (src/client/i18n/dictionary) — falls back to `label` if missing. */
  key?: string;
  href: string;
  children?: NavItem[];
  /**
   * Desktop header: render in the secondary row (next to the store sub-links)
   * instead of the primary row. Mobile nav and footer list it like any other item.
   */
  secondary?: boolean;
  /**
   * Shown only while at least one find is public (GET /api/finds/summary,
   * client hook useFindsLive): the «Hallazgos» entry. See `withFinds`.
   */
  requiresFinds?: boolean;
}

export const NAVIGATION: NavItem[] = [
  {
    // "Shop" heads the subject stores shown in the sub-row (order: estilo,
    // image consulting, belleza, casa). Replaces the old Edit-category sub-row.
    label: 'Shop',
    key: 'nav.shop',
    href: '/shop',
    children: [
      { label: 'Estilo', key: 'nav.estilo', href: '/shop/estilo' },
      // Estilo's one-off pieces (lib/catalog/finds FINDS_PATH), only while one is live.
      { label: 'Hallazgos', key: 'nav.finds', href: '/shop/hallazgos', requiresFinds: true },
      { label: 'Asesoría de imagen', key: 'shop.servicios.title', href: '/caro/servicios' },
      { label: 'Belleza', key: 'nav.beauty', href: '/shop/beauty' },
      { label: 'Casa', key: 'nav.casa', href: '/shop/casa' },
    ],
  },
  { label: 'Edit', key: 'nav.journal', href: '/the-edit' },
  { label: 'Contacto', key: 'nav.contact', href: '/contacto' },
  { label: 'Newsletter', key: 'nav.newsletter', href: '/newsletter', secondary: true },
];

/**
 * Sub-shops shown on the /shop hub (shops by subject, not one global shop).
 * `slug` maps to the i18n keys `shop.<slug>.title|eyebrow|lede`. Product
 * categories point to `/shop/<slug>`; "servicios" is not a product category —
 * it links to Carolina's image-consulting page.
 */
export interface ShopSubject {
  slug: string;
  href: string;
  /** Whether this subject is a product category (renders a product grid). */
  isProductCategory: boolean;
  /**
   * The /shop hub card shows only while a find is public (see NavItem
   * `requiresFinds`). Never on the home's store blocks (`HOME_SUBJECTS`).
   */
  requiresFinds?: boolean;
}

export const SHOP_SUBJECTS: ShopSubject[] = [
  { slug: 'estilo', href: '/shop/estilo', isProductCategory: true },
  // /shop/hallazgos is its own page (FindsPage), not a /shop/:category grid.
  { slug: 'hallazgos', href: '/shop/hallazgos', isProductCategory: false, requiresFinds: true },
  { slug: 'beauty', href: '/shop/beauty', isProductCategory: true },
  { slug: 'casa', href: '/shop/casa', isProductCategory: true },
  { slug: 'servicios', href: '/caro/servicios', isProductCategory: false },
];

/**
 * The home's store blocks ("Selección de CARO"): the permanent subjects only.
 * A conditional entry (Hallazgos) would make the 2×2 grid jump in and out with
 * the stock, and has no square home photo.
 */
export const HOME_SUBJECTS: ShopSubject[] = SHOP_SUBJECTS.filter((s) => !s.requiresFinds);

/** Logical paths of the inline `{key|text}` links in `home.about.body*` (`instagram` = BRAND.instagramUrl). */
export const HOME_ABOUT_LINKS: Readonly<Record<string, string>> = {
  services: '/caro/servicios',
  shop: '/shop',
  letter: '/newsletter',
};

/** `items` without the `requiresFinds` entries unless finds are shown. Pure. */
export function withFinds<T extends { requiresFinds?: boolean }>(items: readonly T[], showFinds: boolean): T[] {
  return items.filter((i) => showFinds || !i.requiresFinds);
}

/**
 * The home poster photo (the LCP element): HomePage renders it, the Worker paints
 * it before JS (worker/lib/prerender `homeHtml`) and index.html preloads the AVIF
 * (kept equal by tests/prerender.test.ts). Served as <picture>: the AVIF (q45,
 * 47 KB) first, the WebP (110 KB) for browsers without AVIF. Both files must be
 * in R2 (`media:upload:new`).
 */
export const HOME_HERO_IMAGE = '/media/caro/carolina-collaer-caro-casa-de-estilo-portada.webp';
export const HOME_HERO_AVIF = '/media/caro/carolina-collaer-caro-casa-de-estilo-portada.avif';

/**
 * One editorial image per shop subject, in two sets: the home "Selección de
 * CARO" blocks (square 1:1 crops) and the /shop hub cards (4:5 crops). Each
 * WebP is cropped to its slot's exact ratio so `FramedImage` fills it. The home blocks
 * ship in two sizes (`-640` + `-1000`, src/lib/images.ts).
 */
export const HOME_STORE_IMAGE: Record<string, string> = {
  estilo: '/media/caro/estilo-look-plumas-salon-caro-casa-de-estilo-1000.webp',
  beauty: '/media/caro/belleza-perfume-y-labial-caro-casa-de-estilo-1000.webp',
  casa: '/media/caro/casa-copas-con-flores-caro-casa-de-estilo-1000.webp',
  servicios: '/media/caro/asesoria-de-imagen-carolina-collaer-abrigo-teddy-1000.webp',
};
export const SHOP_HUB_IMAGE: Record<string, string> = {
  estilo: '/media/caro/estilo-carolina-collaer-camisa-oversize-negra.webp',
  beauty: '/media/caro/belleza-reloj-y-pulseras-caro-casa-de-estilo.webp',
  casa: '/media/caro/casa-cojin-azul-y-sofa-caro-casa-de-estilo.webp',
  servicios: '/media/caro/asesoria-de-imagen-carolina-collaer-camisa-de-cuadros.webp',
  // hallazgos: pending the owner's 4:5 photo; until then the card shows its title.
};

/**
 * The «Hallazgos» card of the /shop hub (client + prerender). Paused by the owner
 * (2026-10-15) until its 4:5 photo and the hub decision: false = no card even with
 * live finds. Menu, ☰, footer and the Estilo teaser are not affected.
 */
export const SHOP_HUB_SHOWS_FINDS = false;

export const ANNOUNCEMENT = {
  active: true,
  // TODO: CONTENT — confirm final shipping threshold with Carolina.
  text: 'Envío gratuito en la eurozona a partir de 50 € · Selección personal de CARO',
} as const;

/** Footer legal links. `key` is the i18n label (falls back to the Spanish `label`). */
export const FOOTER_LEGAL_LINKS: { label: string; key: string; href: string }[] = [
  { label: 'Aviso legal', key: 'footer.legal.notice', href: '/legal/aviso-legal' },
  { label: 'Privacidad', key: 'footer.legal.privacy', href: '/legal/privacidad' },
  { label: 'Cookies', key: 'footer.legal.cookies', href: '/legal/cookies' },
  { label: 'Condiciones de compra', key: 'footer.legal.terms', href: '/legal/condiciones-compra' },
];

/**
 * COLORIMETRY ME! (docs/plan-colorimetry-me.md): where «Descubrir la asesoría de imagen CARO» points
 * after the result and the games. Change it here, not in the game.
 */
export const COLORIMETRY = {
  consultingHref: '/caro/servicios',
} as const;
